import {readable, type Invalidator, type Subscriber, type Unsubscriber} from 'svelte/store';
import {apiCall} from '../api';
import {setAsyncInterval} from '../utils';

export type ApiStore<T> = {
    subscribe(this: void, run: Subscriber<T>, invalidate?: Invalidator<T>): Unsubscriber;
    update: () => Promise<void>;
    setUrl: (newUrl: string) => void;
};

export type ApiStoreData<T> = {
    initialized: boolean;
    error: boolean;
    loading: boolean;
    value: T;
};

export type ApiStoreOptions = {
    request_options?: RequestInit;
    auto_update_timeout?: number;
};

export function apiStore<T>(initial: T, url: string = '', options: ApiStoreOptions = {}): ApiStore<ApiStoreData<T>> {
    const auto_update_timeout = options.auto_update_timeout || 3000;

    let manualUpdate = async () => {};

    const {subscribe} = readable<ApiStoreData<T>>({initialized: false, error: false, loading: true, value: initial}, (set, update) => {
        let updating = false;
        let old_url = url;

        // TODO: Не обновлять стор если значение не изменилось
        // TODO: Использовать отдельный стор для индикации чтобы не перерисовывать все заново

        const loadData = async () => {
            if (!url || updating) {
                return;
            }

            try {
                updating = true;
                update(s => {
                    return {...s, loading: true, ...(old_url !== url ? {initialized: false} : {})};
                });
                old_url = url;
                const res = await apiCall<T>(url, options.request_options);

                if (!res.ok) {
                    update(s => {
                        return {...s, initialized: true, error: true, loading: false};
                    });
                    return;
                }
                set({
                    initialized: true,
                    error: false,
                    loading: false,
                    value: res.value,
                });
            } catch (e) {
                console.error('Error in ApiStore', e);

                update(s => {
                    return {...s, initialized: true, error: true, loading: false};
                });
            } finally {
                updating = false;
            }
        };

        const stopAutoUpdate = setAsyncInterval(async () => {
            await loadData();
        }, auto_update_timeout);

        manualUpdate = loadData;

        return () => {
            stopAutoUpdate();
            manualUpdate = async () => {};
        };
    });

    return {
        subscribe,
        update: async () => manualUpdate(),
        setUrl: (newUrl: string) => (url = newUrl),
    };
}
